import { PromotionListResponseType } from "@sellar-io/api-v2/types";

import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

import { ContextOrderBasketType, useBasketContext } from "../../../../../hooks";

import { isPromotionalOrderItem } from "./isPromotionalOrderItem";

export type StorefrontPromotionContextApi = {
  isFetchingPromotions: boolean;
  promotions: PromotionListResponseType;
  claimedPromotionRewardItemIds: number[];
  claimPromotionRewardItem: (params: {
    promotionRewardItemIds: number[];
    onComplete: (claimedPromotionRewardItemIds: number[]) => void;
  }) => void;
  unclaimPromotionRewardItem: (params: {
    promotionRewardItemIds: number[];
    onComplete: (claimedPromotionRewardItemIds: number[]) => void;
  }) => void;
  applyEligiblePromotionRewards: ({
    order,
    claimedPromotionRewardItemIds,
  }: {
    order: ContextOrderBasketType;
    claimedPromotionRewardItemIds: number[];
  }) => ContextOrderBasketType;
  setPriceTierId: ({ priceTierId }: { priceTierId: number }) => void;
  setSupplierId: ({ supplierId }: { supplierId: number }) => void;
};

export type StorefrontPromotionProviderProps = {
  children: ReactNode;
};

export const StorefrontPromotionContext =
  createContext<StorefrontPromotionContextApi | null>(null);

export const useStorefrontPromotion = () => {
  const context = useContext(StorefrontPromotionContext);

  if (!context) {
    throw new Error(
      "useStorefrontPromotion() must be used within <StorefrontPromotionProvider />",
    );
  }

  return context;
};

export const PROMOTIONS_PRICE_TIER_ID = "PROMOTION_PRICE_TIER_ID";

export const PROMOTIONS_SUPPLIER_ID = "PROMOTION_SUPPLIER_ID";

export const findPromotionRewardsWithSpendThresholdGreaterThanOrderSubtotal = ({
  basketSubtotal,
  claimedPromotionRewardItemIds,
  promotions,
}: {
  basketSubtotal: number;
  promotions: PromotionListResponseType;
  claimedPromotionRewardItemIds: number[];
}) => {
  const claimedPromotionRewardItems = promotions
    .flatMap((promotion) => promotion.PromotionRewardItems)
    .filter((promotionRewardItem) =>
      claimedPromotionRewardItemIds.includes(promotionRewardItem.id),
    );

  const promotionRewardItemIdsToRemove = claimedPromotionRewardItems
    .filter((claimedPromotionRewardItem) => {
      return claimedPromotionRewardItem.spendThreshold > basketSubtotal;
    })
    .map((promotionRewardItem) => promotionRewardItem.id);

  return promotionRewardItemIdsToRemove;
};

export const useClaimedPromotionRewardItemsId = () => {
  const [{ order }] = useBasketContext();
  const initialClaimedPromotionRewardItemIds =
    order?.OrderItems?.filter((orderItem) => isPromotionalOrderItem(orderItem))
      .map((orderItem) => orderItem.PromotionRewardItemId ?? null)
      .filter(Boolean) ?? [];

  const [claimedPromotionRewardItemIds, setClaimedPromotionRewardItemIds] =
    useState<number[]>([]);

  return {
    claimedPromotionRewardItemIds: [
      ...new Set([
        ...initialClaimedPromotionRewardItemIds,
        ...claimedPromotionRewardItemIds,
      ]),
    ],
    setClaimedPromotionRewardItemIds,
  };
};

export const useSyncPromotionPriceTier = ({
  priceTierId,
}: {
  priceTierId: number;
}) => {
  const { setPriceTierId } = useStorefrontPromotion();
  useEffect(() => {
    if (priceTierId) {
      setPriceTierId({ priceTierId });
    }
  }, [priceTierId, setPriceTierId]);

  return null;
};

export const useSyncPromotionSupplierId = ({
  supplierId,
}: {
  supplierId: number;
}) => {
  const { setSupplierId } = useStorefrontPromotion();
  useEffect(() => {
    if (supplierId) {
      setSupplierId({ supplierId });
    }
  }, [setSupplierId, supplierId]);

  return null;
};
